import { uploadFileToS3 } from './uploadFileToS3';
import { dataURLtoFile } from './uploadCroppedImage';
import { resizeImageForUpload } from './resizeImageForUpload';

/**
 * Uploads an annotated image (data URL) to S3
 */
export const uploadAnnotatedImage = async (
  dataUrl: string, 
  originalImageId: string,
  sopId?: string
): Promise<{ media_id: string; upload_url: string }> => {
  // Create a filename based on the original image ID and timestamp
  const timestamp = Date.now();
  const filename = `annotated_${originalImageId}_${timestamp}.png`;
  
  // Convert data URL to File and resize (preserve PNG for transparency)
  const file = dataURLtoFile(dataUrl, filename);
  const resized = await resizeImageForUpload(file, { preserveTransparency: true });

  // Upload to S3
  const result = await uploadFileToS3(resized, sopId);
  
  if (!result || typeof result === 'string') {
    throw new Error('Failed to upload annotated image');
  }
  
  return result;
};

/**
 * Extracts annotated images from editor HTML content
 */
export const extractAnnotatedImages = (htmlContent: string): Array<{
  imageId: string;
  annotatedData: string;
}> => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  const images = tempDiv.querySelectorAll('img[data-is-annotated="true"]');
  const annotatedImages: Array<{ imageId: string; annotatedData: string }> = [];
  
  images.forEach((img) => {
    const annotatedData = img.getAttribute('data-annotated-data');
    let imageId = img.getAttribute('data-image-id');
    
    // If no data-image-id, try to extract from src
    if (!imageId) {
      const src = img.getAttribute('src');
      if (src) {
        // Extract filename from URL
        const urlParts = src.split('/');
        const filename = urlParts[urlParts.length - 1];
        imageId = filename || `image_${Date.now()}`;
      } else {
        imageId = `image_${Date.now()}`;
      }
    }
    
    if (annotatedData && annotatedData.startsWith('data:image/')) {
      annotatedImages.push({
        imageId,
        annotatedData
      });
    }
  });
  
  return annotatedImages;
};

/**
 * Replaces annotated image data URLs with S3 URLs in HTML content
 */
export const replaceAnnotatedImageUrls = (
  htmlContent: string, 
  replacements: Record<string, { url: string; mediaId: string }>
): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  const images = tempDiv.querySelectorAll('img[data-is-annotated="true"]');
  
  images.forEach((img) => {
    const annotatedData = img.getAttribute('data-annotated-data');
    
    if (annotatedData && replacements[annotatedData]) {
      const replacement = replacements[annotatedData];
      
      // Update the src attribute with the S3 URL
      img.setAttribute('src', replacement.url);
      
      // Update data-image-id to point to the new annotated image
      // This ensures that when the URL expires, we fetch the annotated version, not the original
      img.setAttribute('data-image-id', replacement.mediaId);
      
      // Update data attributes to track the uploaded image
      img.setAttribute('data-annotated-media-id', replacement.mediaId);
      img.removeAttribute('data-annotated-data');
    }
  });
  
  return tempDiv.innerHTML;
};

/**
 * Processes annotated images in HTML content by uploading them to S3 and updating references
 */
export const processAnnotatedImages = async (
  htmlContent: string,
  sopId: string,
  getImageUrls: (refIds: string[]) => Promise<Record<string, { url: string; expiresAt: number }>>
): Promise<string> => {
  const annotatedImages = extractAnnotatedImages(htmlContent);
  
  if (annotatedImages.length === 0) {
    return htmlContent;
  }
  
  try {
    const replacements: Record<string, { url: string; mediaId: string }> = {};
    
    // Upload each annotated image
    for (const annotatedImage of annotatedImages) {
      const uploadResult = await uploadAnnotatedImage(
        annotatedImage.annotatedData,
        annotatedImage.imageId,
        sopId
      );
      
      // Get the download URL for the uploaded image
      const downloadUrls = await getImageUrls([uploadResult.media_id]);
      const urlState = downloadUrls[uploadResult.media_id];
      
      if (urlState) {
        replacements[annotatedImage.annotatedData] = {
          url: urlState.url,
          mediaId: uploadResult.media_id
        };
      }
    }
    
    // Replace annotated data URLs with S3 URLs
    return replaceAnnotatedImageUrls(htmlContent, replacements);
  } catch (error) {
    console.error('Error processing annotated images:', error);
    return htmlContent; // Return original content if processing fails
  }
};
